// The live sites we built, grouped by the kind of business, for the
// Websites page (and anywhere else the site shows its work). Each site has
// a short looping recording at phone and desktop size in
// public/assets/sites/video/ (made by the recorder in the session notes:
// load, let the hero animate, scroll down twice, scroll back up), with a
// poster frame next to it. Only sites that are live and that the client is
// happy to show.

// One section per kind of business: what the site is built around, three
// things that follow from that, and the sites that prove it.
export const WORK_GROUPS = [
  {
    id: 'restaurants',
    name: 'Restaurants and food trucks',
    short: 'Restaurants',
    around: 'Built around the order button.',
    text: 'The menu with photos and prices, and an order button that stays on the screen. Every dish gets its own page, so a search for your best seller and your city lands on you.',
    price: '$197', setup: '$500 setup, waived on a one-year contract',
    includes: ['Designed for your restaurant, not a template', 'The menu with photos and prices', 'Online ordering connected to Square, or the system you use', 'Every page written so Google and AI assistants find you', 'English and Spanish, your Google reviews on the page'],
    points: [
      { title: 'Order in two taps', text: 'The order button is on every page and opens the ordering you already use.' },
      { title: 'Found on Google for every dish', text: 'One page per item, written so Google and AI assistants can read it.' },
      { title: 'Open now, and your reviews', text: 'A live open or closed line, and your Google reviews on the home page.' },
    ],
  },
  {
    id: 'furniture',
    name: 'Furniture stores',
    short: 'Furniture',
    around: 'Built around the showroom.',
    text: 'Every piece you carry online with price, size, and stock, and a designer that sells by voice or text, builds the cart, and hands off to a person. Your floor and the site share one catalog.',
    points: [
      { title: 'Every piece, every fact', text: 'Price, dimensions, colours, material, brand, and whether it is in stock.' },
      { title: 'A designer that sells', text: 'Shoppers talk or type, and it filters, compares, and builds the cart.' },
      { title: 'The floor and the phone too', text: 'The in-store sales assistant and the phone agent run from the same catalog.' },
    ],
    more: { label: 'The full story is on the home page', path: '/' },
  },
  {
    id: 'medical',
    name: 'Medical and clinics',
    short: 'Medical',
    around: 'Built around the booking.',
    text: 'The new patient offer is the first thing on the screen, then one tap to book, one tap to call, one tap to the map. Hours, address and insurance notes live in one place, so what the patient reads is always what is true.',
    price: '$497', setup: '$500 setup, waived on a one-year contract',
    includes: ['Designed for your practice, not a template', 'A page for every service you offer', 'Every page written so Google and AI assistants find you', 'Booking and a call button on every screen', 'Hours, address and insurance notes updated in one place'],
    points: [
      { title: 'The offer first', text: 'The new patient special, or the reason to choose you, before anything else.' },
      { title: 'Book and call on every screen', text: 'One tap to book, one tap to call, one tap to the map, on every page.' },
      { title: 'Found for what you treat', text: 'One page per service, written so a search for it and your city lands on you.' },
    ],
  },
  {
    id: 'local',
    name: 'Local services',
    short: 'Local services',
    around: 'Built around the walk-in.',
    price: null, setup: 'Quoted per business. Book 30 minutes and you will have a number by the end of the call.',
    text: 'The reason to choose you first, then one tap to call or get directions. For a registration office it is walk in and skip the DMV line, with a map that shows how close you are.',
    points: [
      { title: 'The offer first', text: 'The reason to choose you is the first thing on the screen.' },
      { title: 'Call and directions on every screen', text: 'One tap to call, one tap to the map.' },
      { title: 'Hours and directions they can trust', text: 'Address, hours, and phone live in one place and update everywhere.' },
    ],
  },
];

// The recording and its poster frame for one site, by slug.
const media = (slug) => ({
  video: { phone: `/assets/sites/video/${slug}-phone.mp4`, desktop: `/assets/sites/video/${slug}-desktop.mp4` },
  poster: { phone: `/assets/sites/video/${slug}-phone.jpg`, desktop: `/assets/sites/video/${slug}-desktop.jpg` },
});

// `accent` is the site's own main colour; the showcase glows with it.
export const WORK = [
  {
    slug: 'drizzled',
    group: 'restaurants',
    name: 'Drizzled Dessert Trailer',
    kind: 'Dessert trailer',
    place: 'Stockton, CA',
    href: 'https://drizzled209.com/',
    domain: 'drizzled209.com',
    accent: '#E8A0B8',
    line: 'Menu with photos and prices, one page per dessert, order online on every screen, English and Spanish.',
    result: '75% more online orders',
    ...media('drizzled'),
  },
  {
    slug: 'smash-n-daaash',
    group: 'restaurants',
    name: 'Smash N Daaash',
    kind: 'Burger truck',
    place: 'Stockton, CA',
    href: 'https://smashndaaash.vercel.app/',
    domain: 'smashndaaash.vercel.app',
    accent: '#FF5A24',
    line: 'A burger truck off I-5. Live open or closed line, order ahead for pickup, English and Spanish.',
    ...media('smash-n-daaash'),
  },
  {
    slug: 'east-bay',
    group: 'furniture',
    name: 'East Bay Furniture',
    kind: 'Furniture store',
    place: 'Pittsburg, CA',
    href: 'https://eastbay.direct/',
    domain: 'eastbay.direct',
    accent: '#6B8F5E',
    line: 'The whole showroom online with a designer that sells by voice or text, checkout, and delivery across Northern California.',
    ...media('east-bay'),
  },
  {
    slug: 'carmichael',
    group: 'furniture',
    name: 'Carmichael Furniture Outlet',
    kind: 'Furniture store',
    place: 'Carmichael, CA',
    href: 'https://www.carmichaelfurnitureoutlet.com/',
    domain: 'carmichaelfurnitureoutlet.com',
    accent: '#E04B4B',
    line: 'Collections, reviews, and directions for an outlet that sells on the floor.',
    ...media('carmichael'),
  },
  {
    slug: 'davis',
    group: 'furniture',
    name: 'Davis Furniture Outlet',
    kind: 'Furniture store',
    place: 'Davis, CA',
    href: 'https://yourdavisfurniture.com/',
    domain: 'yourdavisfurniture.com',
    accent: '#F5B81E',
    line: 'Built for students and families looking for outlet prices near campus.',
    ...media('davis'),
  },
  {
    slug: 'feidy',
    group: 'medical',
    name: 'Feidy Chiropractic',
    kind: 'Chiropractic clinic',
    place: 'Marietta, GA',
    href: 'https://www.feidychiropractic.com/',
    domain: 'feidychiropractic.com',
    accent: '#3B8BEA',
    line: 'A new patient offer front and center, booking and a call button on every screen.',
    ...media('feidy'),
  },
  {
    slug: 'agile',
    group: 'local',
    name: 'Agile Registration Services',
    kind: 'DMV services',
    place: 'Stockton, CA',
    href: 'https://agileregistrations.com/',
    domain: 'agileregistrations.com',
    accent: '#E0AE2E',
    line: 'Walk in, skip the DMV line. English and Spanish.',
    ...media('agile'),
  },
  {
    slug: 'j-brothers',
    group: 'local',
    name: 'J Brothers Registration',
    kind: 'DMV services',
    place: 'Modesto, CA',
    href: 'https://jbrothersregistration.com/',
    domain: 'jbrothersregistration.com',
    accent: '#E23B47',
    line: 'Directions and a call button first, with a map that shows how close they are to the DMV.',
    ...media('j-brothers'),
  },
];

export const workIn = (groupId) => WORK.filter((w) => w.group === groupId);
